import { useMemo, useRef } from 'react';
import { mergeProps, useFocusRing, useToggleButton } from 'react-aria';
import { useToggleState } from 'react-stately';

import { noop } from '../../lib/noop';
import { getToggleImages } from '../controls/toggle/getToggleImages';

import { useHover } from '../../hooks/useHover.js';

import styles from './DeviceQuickAction.module.css';

import type { DeviceQuickActionProps } from './DeviceQuickAction.types';
import { getMediaDeviceQuickActionColors } from './DeviceQuickActionColors';
import type { DeviceQuickActionStyle } from './DeviceQuickActionColors';
import { DeviceQuickActionIcon } from './DeviceQuickActionIcon';

export function DeviceQuickActionMediaToggle(props: DeviceQuickActionProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const isVoidButton = props.capabilityObj?.setable !== true;
  const state = useToggleState({ isSelected: props.isActive });
  const hover = useHover({});
  const toggleButton = useToggleButton(
    {
      'aria-label': props.label,
      isDisabled: props.isDisabled === true,
      isSelected: props.isActive,
      onPress: isVoidButton === true ? noop : props.onPress,
    },
    state,
    ref,
  );
  const focusRing = useFocusRing({});
  const buttonProps =
    props.isDisabled === true
      ? mergeProps(toggleButton.buttonProps, focusRing.focusProps)
      : mergeProps(toggleButton.buttonProps, focusRing.focusProps, hover.hoverProps);
  const toggleImages =
    props.capabilityObj != null
      ? getToggleImages(props.capabilityObj.id, props.capabilityObj)
      : null;
  const style = useMemo(() => {
    const colors = getMediaDeviceQuickActionColors({
      isActive: props.isActive,
      isHovered: false,
      isTileOff: props.isTileOn === false,
    });
    const hoverColors = getMediaDeviceQuickActionColors({
      isActive: props.isActive,
      isHovered: true,
      isTileOff: props.isTileOn === false,
    });

    return {
      '--prop-background-color': colors.backgroundColor,
      '--prop-border-color': colors.borderColor,
      '--prop-hover-background-color': hoverColors.backgroundColor,
      '--prop-hover-border-color': hoverColors.borderColor,
      '--prop-icon-color': colors.iconColor,
      '--prop-icon-size': colors.iconSize,
      '--prop-hover-icon-color': hoverColors.iconColor,
      '--prop-outline-color': colors.outlineColor,
      '--prop-overlay-color': colors.overlayColor,
      '--prop-hover-overlay-color': hoverColors.overlayColor,
    } as DeviceQuickActionStyle;
  }, [props.isActive, props.isTileOn]);

  return (
    <button
      {...buttonProps}
      ref={ref}
      className={styles.root}
      style={style}
      data-is-focus-visible={focusRing.isFocusVisible === true ? '' : undefined}
      data-is-hovered={props.isDisabled === true ? false : hover.isHovered}
      data-is-active={props.isActive === true ? '' : undefined}
      data-variant={props.variant}
      title={props.label}
      type="button"
    >
      <DeviceQuickActionIcon
        iconObj={toggleImages?.iconObj}
        source={props.isActive === true ? toggleImages?.urlTrue : toggleImages?.urlFalse}
        useIconObj={toggleImages?.useIconObj === true}
      />
    </button>
  );
}
